import { getLogger } from '@sas/nova-core/logging';
import { useState, useEffect, useCallback } from 'react';
import { AvatarDataProvider } from '../';
import { UserType } from '@sas/nova-core/services';

const logger = getLogger('Avatar');

const getAvatarKey = (id: string, type: UserType = 'user') => `${id}|${type}`;

const onUpdateListenersMap = new Map<
  string,
  { lastModifiedTime?: number; listeners: Array<(lastModifiedTime: number) => void> }
>();

const updateAvatar = (id: string, type: UserType = 'user'): void => {
  const avatarKey = getAvatarKey(id, type);
  const avatarOnUpdateListeners = onUpdateListenersMap.get(avatarKey);
  if (!avatarOnUpdateListeners) return;

  const currentTime = Date.now();
  avatarOnUpdateListeners.lastModifiedTime = currentTime;

  if (!avatarOnUpdateListeners.listeners) return;
  for (const onUpdateListener of avatarOnUpdateListeners.listeners) {
    onUpdateListener(currentTime);
  }
};

export const usePublishAvatarUpdate = (
  dataProvider: AvatarDataProvider,
  id?: string,
  type?: UserType
): (() => void) => {
  const actualId = useUserGroupActualId(dataProvider, id, type);
  return () => {
    if (!actualId) return;
    updateAvatar(actualId, type);
  };
};

export const useOnAvatarUpdate = (
  dataProvider: AvatarDataProvider,
  onUpdate: () => void,
  id?: string | undefined,
  type?: UserType | undefined,
  fetchedTime?: number | undefined
): void => {
  const actualId = useUserGroupActualId(dataProvider, id, type);
  const onUpdateListener = useCallback(
    (lastModifiedTime: number) => {
      if (!fetchedTime || lastModifiedTime > fetchedTime) {
        onUpdate();
      }
    },
    [fetchedTime, onUpdate]
  );

  const avatarKey = getAvatarKey(actualId, type);

  useEffect(() => {
    if (!fetchedTime) {
      return;
    }
    const avatarOnUpdateListeners = onUpdateListenersMap.get(avatarKey);
    const lastModifiedTime = avatarOnUpdateListeners?.lastModifiedTime;
    if (lastModifiedTime && lastModifiedTime > fetchedTime) {
      onUpdate();
    }
  }, [fetchedTime, avatarKey, onUpdate]);

  useEffect(() => {
    if (!onUpdateListenersMap.has(avatarKey)) {
      onUpdateListenersMap.set(avatarKey, { listeners: [] });
    }

    const { listeners } = onUpdateListenersMap.get(avatarKey)!;
    listeners.push(onUpdateListener);

    return () => {
      const avatarOnUpdateListeners = onUpdateListenersMap.get(avatarKey)!;
      avatarOnUpdateListeners.listeners = avatarOnUpdateListeners.listeners.filter(
        (listener) => listener !== onUpdateListener
      );
    };
  }, [avatarKey, onUpdateListener]);
};

// There are cases where id is different from its actual id.
// For example, "@currentUser" is a special id for Viya and its actual id is dependent on the current user signed in
export const useUserGroupActualId = (
  dataProvider: AvatarDataProvider,
  id?: string,
  type?: UserType
): string => {
  const [actualId, setActualId] = useState<string | undefined>(id);
  useEffect(() => {
    let ignoreResult = false;
    (async () => {
      if (!dataProvider.resolveId) {
        setActualId(id);
        return;
      }
      try {
        const actualId = await dataProvider.resolveId(id, type);
        if (ignoreResult) return;
        setActualId(actualId);
      } catch (e) {
        if (ignoreResult) return;
        setActualId(id);
        logger.error(`Failed to get the actual id for id:${id} and type: ${type}`);
      }
    })();
    return () => {
      ignoreResult = true;
    };
  }, [dataProvider, id, type]);
  return actualId ?? '';
};
